import { InlineLoading, Tag } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import type { TcScenarioInventorySummary } from 'types/dps-capture';
import styles from './capture.module.scss';

interface InventoryChipsProps {
  summary: TcScenarioInventorySummary | null;
  loading: boolean;
  /** False before any Type Curve Scenario is picked — the prompt state (AC 1). */
  hasSelection: boolean;
}

/**
 * Org-wide scope chips under the Type Curve Scenario dropdown. Before a scenario
 * is selected it shows the prompt (no compute); once selected it renders the
 * inventory counts with a labeled base ("in scope", not "scheduled").
 *
 * @param {InventoryChipsProps} props - The summary, load state, and selection flag.
 * @return {JSX.Element} The chips, prompt, or in-flight indicator.
 */
export function InventoryChips({
  summary,
  loading,
  hasSelection,
}: InventoryChipsProps): JSX.Element {
  if (!hasSelection) {
    return (
      <p className={styles.prompt}>
        Select a Type Curve Scenario to see its inventory scope
      </p>
    );
  }

  if (loading && !summary) {
    return <InlineLoading description="Loading inventory scope…" />;
  }

  if (!summary) return <div className={styles.chips} />;

  return (
    <div className={styles.chips} aria-busy={loading}>
      <Tag type="blue">
        <FormattedCount value={summary.scheduledSlotCount} /> slots in scope
      </Tag>
      <Tag type="teal">
        <FormattedCount value={summary.ltdLocationCount} /> LTD locations
      </Tag>
      <Tag type="cool-gray">
        <FormattedCount value={summary.unitCount} /> units
      </Tag>
      {loading && <InlineLoading description="Updating…" />}
    </div>
  );
}
